import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@contexts/AuthContext';
import AuthGateModal from '@components/auth/AuthGateModal';

interface AuthGateContextValue {
  /** True while the visitor is unauthenticated — gated content stays blurred. */
  isGated: boolean;
  /** True while the sign-in prompt is on screen. */
  isPromptOpen: boolean;
  /** True once the visitor has closed the prompt at least once this session. */
  hasDismissed: boolean;
  /** Show the prompt. No-op for signed-in users. */
  openPrompt: () => void;
  /** Show the prompt only if the visitor hasn't already dismissed it (passive triggers). */
  openPromptOnce: () => void;
  closePrompt: () => void;
}

const AuthGateContext = createContext<AuthGateContextValue | null>(null);

/**
 * Drives the "sign in to keep reading" gate.
 *
 * Sits below AuthProvider so it can read the session, and renders the prompt
 * itself so any gated region — or any control pointing at one — can raise it
 * without threading props through the page.
 */
export function AuthGateProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);

  // Gated until proven otherwise — the content must never flash unblurred
  // while the session check is still in flight. The prompt itself waits for
  // that check to settle so signed-in visitors never see it.
  const isGated = !isAuthenticated;
  const canPrompt = isGated && !isLoading;

  const openPrompt = useCallback(() => {
    if (canPrompt) setIsPromptOpen(true);
  }, [canPrompt]);

  const openPromptOnce = useCallback(() => {
    if (canPrompt && !hasDismissed) setIsPromptOpen(true);
  }, [canPrompt, hasDismissed]);

  const closePrompt = useCallback(() => {
    setIsPromptOpen(false);
    setHasDismissed(true);
  }, []);

  const value = useMemo(
    () => ({ isGated, isPromptOpen, hasDismissed, openPrompt, openPromptOnce, closePrompt }),
    [isGated, isPromptOpen, hasDismissed, openPrompt, openPromptOnce, closePrompt]
  );

  return (
    <AuthGateContext.Provider value={value}>
      {children}
      <AuthGateModal isOpen={isGated && isPromptOpen} onClose={closePrompt} />
    </AuthGateContext.Provider>
  );
}

export function useAuthGate(): AuthGateContextValue {
  const context = useContext(AuthGateContext);
  if (!context) throw new Error('useAuthGate must be used within AuthGateProvider');
  return context;
}
